import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  getPlantDetails,
  getSheetColumns,
  getProceedToAnalysis,
  getPlantReadiness,
} from "../../network/cipAPI";
import {
  storeFileId,
  storeApp,
  storeDefaultInformation,
  storeUnits,
  storePlantData,
} from "../../Store/TemplateStore/template.action";
import Header from "../../components/Header";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import {
  SettingsIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "../../assets/icons";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChanges";
import PerformanceData from "../performanceData";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { ValueStrings } from "../../utils/constantsString";
import {
  extractPlantId,
  getSelectedPlantTrainInfo,
  getDatasetDetails,
  updatePlantDataTrain,
} from "../../utils/Utils";
import { getItemSession, removeLocalStorageItem } from "../../utils/session";
import ColumnSelection from "./columnSelection";
import ReferenceCondition from "./referenceCondition";
import DefaultCIPInformation from "../defaultCIPInformation";
import {
  DEFAULT_CIP_KEYS,
  NORMALIZATION_TYPES,
  CustomModalButtons,
} from "../../constants/global.constants";
import AdvanceSettings from "./advancedSettings";
import ConfirmationPopup from "../../packages/ConfirmationPopup";
import { API_URLS } from "../../network/appUrls";
import FileUpload from "../fileUpload";
import CIPSpecification from "../../components/CIPSpecification";
import DataAppend from "../../components/DataAppend/DataAppned";
import PlantChecklist from "../../components/PlantChecklist";
import Footer from "../../common/footer/Footer";
import CircularLoader from "../../common/styles/components/circularLoader";

import "./index.scss";

const basePath = API_URLS.basePath;

const AnalyseData = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const plantName = getItemSession("plantName");
  const waterType = getItemSession("water_type");

  const data = location.state;
  const searchParams = new URLSearchParams(window.location.search);
  const isEdit = searchParams.get("isEdit");
  const showRecurrent = data?.showRecurrent || false;
  const { token, plantData } = useSelector((state) => state);
  const [columnOptions, setColumnOptions] = useState("");
  const [fileId, setFileId] = useState("");
  const [mappedColumn, setMappedColumn] = useState([]);
  const [config, setConfig] = useState([]);
  const [mappedReference, setMappedReference] = useState([]);
  const [tempReferenceValues, setTempReferenceValues] = useState({});
  const [datasetDetails, setDatasetDetails] = useState("");
  const [fileInfo, setFileInfo] = useState([]);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isNormalizationColumnFilled, setIsNormalizationColumnFilled] =
    useState(false);
  const [isFTNormFallbackDialogOpen, setIsFTNormFallbackDialogOpen] =
    useState(false);
  const [normSelectionType, setNormSelectionType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedColumnRadioColumn, setSelectedColumnRadioColumn] = useState({
    column5: "feed_flowrate",
    column8: "concentrate_pressure",
  });
  const plantId = extractPlantId();
  const sel_train = getSelectedPlantTrainInfo(plantData, plantId);
  const [selectedTrain, setSelectedTrain] = useState(sel_train || 1);
  const [disableTrain, setDisableTrain] = useState(true);
  const [selectedSheet, setSelectedSheet] = useState();
  const [selectedCIPFileName, setSelectedCIPFileName] = useState(null);
  const [appendFileList, setAppendFileList] = useState([]);
  const [isappendFile, setIsAppendFile] = useState(null);
  const [appendStatus, setAppendStatus] = useState("idle");
  const [hasRecurrentCIPs, setHasRecurrentCIPs] = useState(false);
  const [ownerUserName, setOwnerUserName] = useState("");
  const [mandatoryComponentsStatus, setMandatoryComponentsStatus] = useState({
    columnMapping: {
      fulfilled: false,
      changed: false,
      error: false,
    },
    referenceConditions: {
      fulfilled: false,
      changed: false,
      error: false,
    },
    defaultCIPInfo: {
      fulfilled: false,
      changed: false,
      error: false,
    },
  });
  const [showPlantChecklist, setShowPlantChecklist] = useState(false);
  const [plantChecklistData, setPlantChecklistData] = useState([]);

  const handleSettingsDialogOpen = () => {
    setIsSettingsDialogOpen(true);
  };
  const handleSettingsDialogClose = () => {
    setIsSettingsDialogOpen(false);
  };

  const getFormattedDate = (dateString) => {
    const date = moment(dateString).isValid();
    return date ? moment(dateString).format(ValueStrings.dateFormat) : "";
  };

  const onExitClick = async () => {
    navigate(`${basePath}editPlant?plantId=${plantId}&isEdit=true`);
  };

  const hasUnsavedChanges = useMemo(() => {
    return Object.values(mandatoryComponentsStatus).some(
      (status) => status.changed === true,
    );
  }, [mandatoryComponentsStatus]);

  const getHasIncompleteComponents = (multiTrainStatus) => {
    const { columnMapping, referenceConditions, defaultCIPInfo } =
      mandatoryComponentsStatus;

    const selTrainStatus = {
      column_mapping:
        columnMapping.fulfilled &&
        !columnMapping.changed &&
        !columnMapping.error,
      reference_conditions:
        referenceConditions.fulfilled &&
        !referenceConditions.changed &&
        !referenceConditions.error,
      default_cip_information:
        defaultCIPInfo.fulfilled &&
        !defaultCIPInfo.changed &&
        !defaultCIPInfo.error,
    };

    const allTrainData =
      multiTrainStatus.length > 0
        ? multiTrainStatus.map((status) =>
            status.train === selectedTrain
              ? { train: selectedTrain, ...selTrainStatus }
              : status,
          )
        : [{ train: selectedTrain, ...selTrainStatus }];

    setPlantChecklistData(allTrainData);

    const multiTrainHasIncompleteComponents = allTrainData.some((trainStatus) =>
      Object.values(trainStatus).includes(false),
    );

    return multiTrainHasIncompleteComponents;
  };

  const onProceedClick = async () => {
    setIsLoading(true);
    try {
      let multiTrainStatus = [];

      const response = await getPlantReadiness();
      if (response && response.data) {
        multiTrainStatus = response.data;
      }
      const hasIncompleteComponents =
        getHasIncompleteComponents(multiTrainStatus);

      if (hasIncompleteComponents) {
        setShowPlantChecklist(true);
      } else {
        const response = await getProceedToAnalysis(fileId, selectedTrain);
        if (response && response.status === 200) {
          const { norm_applied_type: appliedType } = response.data;
          if (appliedType === NORMALIZATION_TYPES.FT_NORM_FALLBACK) {
            setIsFTNormFallbackDialogOpen(true);
          } else {
            dispatch(
              storePlantData(
                updatePlantDataTrain(plantData, plantId, selectedTrain),
              ),
            );
            const path = `${basePath}home?plantId=${plantId}`;
            navigate(path);
          }
        } else {
          createNotification(
            ERROR_NOTIFICATION,
            response.data.detail.error,
            "",
          );
        }
      }
    } catch (error) {
      setIsLoading(false);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFTNormFallbackClose = (action) => {
    setIsFTNormFallbackDialogOpen(false);
    if (action === CustomModalButtons.CONFIRM) {
      dispatch(
        storePlantData(updatePlantDataTrain(plantData, plantId, selectedTrain)),
      );
      const path = `${basePath}home?plantId=${plantId}`;
      navigate(path);
    }
  };

  useEffect(() => {
    if (selectedTrain === undefined || selectedTrain === null) {
      setSelectedTrain(1);
    }
  }, [selectedTrain]);

  useEffect(() => {
    const getPlantDetail = async () => {
      setIsLoading(true);
      try {
        const response = await getPlantDetails(plantId, true, sel_train);
        if (response && response.data) {
          const {
            file,
            parent_file_name,
            col_mapping,
            sheet_names,
            bulk_cip_file_name,
            has_recurrent_cips,
            config,
            default_cip,
            ref_condition,
            name: plantName,
            water_type,
            append_files,
            isAppended,
            setting,
            norm_selection_type,
            owner_username,
          } = response.data;

          let file_id, sheet_name, min_date, max_date;
          if (file) {
            ({ file_id, sheet_name, min_date, max_date } = file);
          }

          const fileInfo = {
            file_id,
            sheetName: sheet_name,
            fileName: parent_file_name,
          };
          dispatch(storeUnits(setting));
          dispatch(storeFileId(fileInfo));
          setFileInfo(fileInfo);
          setSelectedSheet(sheet_name);
          setFileId(file_id);
          setMappedColumn(col_mapping);
          setAppendFileList(append_files);
          setIsAppendFile(isAppended);
          isAppended && setAppendStatus("success");
          setSheetNames(sheet_names);
          setSelectedTrain(sel_train);
          setSelectedCIPFileName(bulk_cip_file_name);
          setHasRecurrentCIPs(has_recurrent_cips);
          setNormSelectionType(norm_selection_type);
          setConfig(config);
          setMappedReference(ref_condition?.[0]);
          setOwnerUserName(owner_username);
          setDatasetDetails(
            getDatasetDetails({
              owner_username,
              plantName,
              water_type,
              min_date,
              max_date,
            }),
          );
          if (default_cip) {
            dispatch(storeDefaultInformation(default_cip));
          }

          if (file_id) {
            const columns = await getSheetColumns(file_id, sel_train);
            if (columns && columns.data) {
              setColumnOptions(columns.data?.cols);
            }
          }
        }
      } catch (err) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      } finally {
        setIsLoading(false);
      }
    };
    dispatch(storeApp(null));
    if (token) getPlantDetail();
  }, [token]);

  useEffect(() => {
    if (showRecurrent) {
      setHasRecurrentCIPs(true);
    }
  }, [showRecurrent]);

  useEffect(() => {
    if (!fileId) {
      setDatasetDetails(` ${ownerUserName}_ ${plantName}_ ${waterType}`);
    }
  }, [fileId]);

  useEffect(() => {
    return () => {
      removeLocalStorageItem("prevTrainConfig");
    };
  }, []);

  useUnsavedChangesBlocker(hasUnsavedChanges);

  return (
    <div className="container-main">
      <Header />
      <div className="main-container ">
        <div className="content-container descriptionText font-14">
          {isLoading && (
            <div className="circular-loader-overlay">
              <CircularLoader />
            </div>
          )}
          <div className="headerTealText">{t("analyseData.dataSetup")}</div>
          <PerformanceData
            fileId={fileId}
            fileInfo={fileInfo}
            setFileInfo={setFileInfo}
            selectedTrain={selectedTrain}
            setSelectedTrain={setSelectedTrain}
            setSelectedCIPFileName={setSelectedCIPFileName}
            setHasRecurrentCIPs={setHasRecurrentCIPs}
            setDisableTrain={setDisableTrain}
            setSelectedSheet={setSelectedSheet}
            setMappedColumn={setMappedColumn}
            setMappedReference={setMappedReference}
            sheetNames={sheetNames}
            setSheetNames={setSheetNames}
            setColumnOptions={setColumnOptions}
            setFileId={setFileId}
            setIsAppendFile={setIsAppendFile}
            setAppendFileList={setAppendFileList}
          />
          <FileUpload
            fileInfo={fileInfo}
            config={config}
            setDatasetDetails={setDatasetDetails}
            setSelectedTrain={setSelectedTrain}
            selectedTrain={selectedTrain}
            disableTrain={disableTrain}
            selectedSheet={selectedSheet}
            sheetNames={sheetNames}
            setFileId={setFileId}
            setMappedColumn={setMappedColumn}
            setColumnOptions={setColumnOptions}
            setMappedReference={setMappedReference}
            setSelectedSheet={setSelectedSheet}
            setSelectedCIPFileName={setSelectedCIPFileName}
            setHasRecurrentCIPs={setHasRecurrentCIPs}
            setSheetNames={setSheetNames}
            setIsLoading={setIsLoading}
            setIsAppendFile={setIsAppendFile}
            setAppendFileList={setAppendFileList}
            setAppendStatus={setAppendStatus}
            hasUnsavedChanges={hasUnsavedChanges}
            setNormSelectionType={setNormSelectionType}
          />

          <div className="flex-ai-center padding10">
            <span className="tealText">{t("analyseData.datasetName")} </span>
            &nbsp;
            {datasetDetails}
          </div>

          <ColumnSelection
            columnOptions={columnOptions}
            setColumnOptions={setColumnOptions}
            fileId={fileId}
            isEdit={isEdit}
            mappedColumn={mappedColumn}
            setMappedColumn={setMappedColumn}
            mappedReference={mappedReference}
            setTempReferenceValues={setTempReferenceValues}
            setDatasetDetails={setDatasetDetails}
            plantName={plantName}
            waterType={waterType}
            selectedRadioColumn={selectedColumnRadioColumn}
            setSelectedRadioColumn={setSelectedColumnRadioColumn}
            setMappedReference={setMappedReference}
            setIsNormalizationColumnFilled={setIsNormalizationColumnFilled}
            config={config}
            setSelectedTrain={setSelectedTrain}
            selectedTrain={selectedTrain}
            setMandatoryComponentsStatus={setMandatoryComponentsStatus}
            normSelectionType={normSelectionType}
            ownerUserName={ownerUserName}
          />

          <DataAppend
            title={t("dataAppend.title")}
            content={t("dataAppend.content")}
            buttonText={t("dataAppend.appendData")}
            linkText={t("dataAppend.linkText")}
            selectedTrain={selectedTrain}
            fileId={fileId}
            fileName={fileInfo.fileName}
            setIsLoading={setIsLoading}
            mappedReference={mappedReference}
            appendFileList={appendFileList}
            isappendFile={isappendFile}
            appendStatus={appendStatus}
            setIsAppendFile={setIsAppendFile}
            setAppendFileList={setAppendFileList}
            setAppendStatus={setAppendStatus}
            setTempReferenceValues={setTempReferenceValues}
          />

          <ReferenceCondition
            fileId={fileId}
            isEdit={isEdit}
            mappedReference={mappedReference}
            tempReferenceValues={tempReferenceValues}
            selectedColumnRadioColumn={selectedColumnRadioColumn}
            selectedTrain={selectedTrain}
            setMandatoryComponentsStatus={setMandatoryComponentsStatus}
          />

          <DefaultCIPInformation
            selectedTrain={selectedTrain}
            defaultCIPKeys={DEFAULT_CIP_KEYS}
            setMandatoryComponentsStatus={setMandatoryComponentsStatus}
            noOfRONFElements={mappedColumn?.sel_element_involved}
            selectedPass={mappedColumn?.sel_pass}
            selectedStage={mappedColumn?.sel_stage}
          />

          <CIPSpecification
            selectedTrain={selectedTrain}
            selectedSheet={selectedSheet}
            selectedCIPFileName={selectedCIPFileName}
            hasRecurrentCIPs={hasRecurrentCIPs}
            shouldScrollToRecurrent={showRecurrent}
            mappedReference={mappedReference}
          />

          <div className="margin-top-20 flex-between">
            <WPTertiaryButton
              customClass="margin-left-10"
              onClick={handleSettingsDialogOpen}
              label={t("analyseData.settings")}
              icon={<SettingsIcon />}
              iconPlacement="before"
              size="medium"
              className="margin-right-20"
              variant="outlined"
            />
            <div>
              <WPTertiaryButton
                id="back-to-plant-design-btn"
                label={t("analyseData.backtoPlantDesign")}
                onClick={onExitClick}
                icon={<ArrowLeftIcon />}
                iconPlacement="before"
                size="medium"
                className="margin-right-20"
                variant="outlined"
              />
              <WPTertiaryButton
                onClick={onProceedClick}
                label={t("analyseData.proceedtoAnalysis")}
                icon={<ArrowRightIcon />}
                iconPlacement="after"
                variant="contained"
                type="secondary"
                size="medium"
              />
            </div>
          </div>
        </div>

        {isSettingsDialogOpen && (
          <AdvanceSettings
            isSettingsDialogOpen={isSettingsDialogOpen}
            handleSettingsDialogClose={handleSettingsDialogClose}
            selectedTrain={selectedTrain}
            outlierRemoval
            cipAndShutdownIdentification
          />
        )}
        {showPlantChecklist && (
          <PlantChecklist
            setShowPlantChecklist={setShowPlantChecklist}
            checkListData={plantChecklistData}
          />
        )}
        {isFTNormFallbackDialogOpen && (
          <ConfirmationPopup
            header={t("analyseData.dataNormalizationForAnalysis")}
            description={t("analyseData.ftNormFallbackMessage")}
            confirmBtn={t("ValueStrings.okay")}
            onClose={handleFTNormFallbackClose}
            hideCancel
            type="info"
            width="500px"
          />
        )}
        <Footer />
      </div>
    </div>
  );
};

export default AnalyseData;
